<template>
  <svg ref="svg" :width="width" :height="height" >
    <g v-if="projection" :transform="zoomable ? zoomTransform : null" >
      <g class="map" >
        <popover
           v-if="featureContent"
           v-for="(p, i) in path"
           :key="i"
           :delay="400"
           :content="featureContent(p.feature)"
           :data="featureData(p.feature)" >
          <path :d="p.path" :fill="p.color" />
        </popover>
        <path v-else v-for="(p, i) in path" :d="p.path" :fill="p.color" :key="i" />
      </g>
      <g class="points" >
        <g class="point"
           v-for="d in data"
           @click="$emit('point-click', d)" >
          <circle
             :fill="color(d)"
             :r="scale(d.value)"
             :cx="projection([d.lng, d.lat])[0]"
             :cy="projection([d.lng, d.lat])[1]" />
          <ellipsis
             v-if="typeof d.label !== 'undefined'"
             :x="projection([d.lng, d.lat])[0]"
             :y="projection([d.lng, d.lat])[1]"
             dy="5"
             text-anchor="middle"
             :text="format ? format(d) : d.label"
             :width="scale(d.value) * 2"
             :fill="textColor(d)"
             :no-popover="!pointLabelPopover" />
        </g>
      </g>
    </g>
  </svg>
</template>

<script>
import * as d3 from 'd3'
import Ellipsis from './Ellipsis'
import Popover from './Popover'
import Zoom from '../mixins/zoom'

export default {
  components: {
    Ellipsis,
    Popover
  },
  mixins: [
    Zoom
  ],
  props: {
    zoomable: {
      type: Boolean,
      default: true
    },
    format: {
      type: Function,
      required: false
    },
    data: {
      required: true
    },
    pointLabelPopover: {
      type: Boolean,
      default: true
    },
    colors: {
      type: Array,
      default: () => [
        '#e6e6e6',
        '#cccccc',
        '#b3b3b3',
        '#a6a6a6',
        '#999999',
        '#808080',
      ],
    },
    width: {
      type: Number,
      required: true
    },
    height: {
      type: Number,
      required: true
    },
    geojson: {
      type: Object,
      required: true
    },
    color: {
      type: Function,
      default: d => '#F0F'
    },
    featureColor: {
      type: Function,
      default: null
    },
    featureContent: {
      default: null
    },
    featureData: {
      default: null
    },
    textColor: {
      type: Function,
      default: d => 'white'
    },
    minRadius: {
      type: Number,
      default: 5
    },
    maxRadius: {
      type: Number,
      default: 30
    },
    domain: {
      type: Array
    }
  },
  data () {
    return {
      geoPath: null,
      projection: null,
    }
  },
  mounted () {
    this.computeProjection()
  },
  watch: {
    geojson: {
      deep: true,
      handler () {
        this.computeProjection()
      }
    },
    width () {
      this.computeProjection()
    },
    height () {
      this.computeProjection()
    }
  },
  computed: {
    scale () {
      const values = this.data.map(d => d.value)
      return d3
        .scaleLinear()
        .domain(this.domain || [Math.min(...values), Math.max(...values)])
        .range([this.minRadius, this.maxRadius])
    },
    path () {
      if (!this.geojson) {
        return []
      }
      if (null === this.geoPath) {
        this.geoPath = d3.geoPath().projection(this.projection)
      }
      const neighbors = this.geojson.features.map(feature => {
        return {
          bounds: this.geoPath.bounds(feature),
          feature: feature
        }
      })
      const colors = {}
      this.colors.forEach(color => {
        colors[color] = []
      })
      const response = this.geojson.features.map((feature, i) => {
        const elementNeighbors = neighbors.filter((neighbor, j) => {
          if (i === j) {
            return false
          }
          return this.overlap(neighbor.bounds, neighbors[i].bounds)
        })

        let color;

        if (!this.featureColor) {
          const availableColors = Object.keys(colors).filter(color => {
            return elementNeighbors.every(neighbor => {
              return colors[color].indexOf(neighbor.feature) === -1
            })
          })

          if (!availableColors.length) {
            availableColors.splice(0, 0, ...Object.keys(colors))
          }

          color = availableColors[0]
          colors[color].push(feature)
        } else {
          color = this.featureColor(feature)
        }

        return {
          feature: feature,
          path: this.geoPath(feature),
          color: color
        }
      })
      return response
    }
  },
  methods: {
    computeProjection () {
      if (!this.geojson) {
        this.projection = () => [0, 0]
      }
      const proj = d3.geoConicConformal().scale(1).translate([0, 0])
      this.geoPath = d3.geoPath().projection(proj)
      const bounds = this.geoPath.bounds(this.geojson)
      const scale = 0.95 / Math.max(
        (bounds[1][0] - bounds[0][0]) / this.width,
        (bounds[1][1] - bounds[0][1]) / this.height
      )
      const translate = [
        (this.width - scale * (bounds[1][0] + bounds[0][0])) / 2,
        (this.height - scale * (bounds[1][1] + bounds[0][1])) / 2
      ]
      proj.scale(scale).translate(translate)
      this.projection = proj
    },
    overlap (a, b) {
      if (!(this.inRange(a[0][0], b[0][0], b[1][0]) || this.inRange(b[0][0], a[0][0], a[1][0]))) {
        return false
      }
      return this.inRange(a[0][1], b[0][1], b[1][1]) || this.inRange(b[0][1], a[0][1], a[1][1])
    },
    inRange(value, min, max) {
      return value >= min && value <= max
    }
  }
}
</script>

<style scoped>
path {
    stroke: black;
}
.point {
    cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// node_modules/d3vue/components/Map.vue